import { useState, useEffect } from 'react';
import { Calendar, Download, FileText, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { formatCurrency } from '../hooks/useLoanRepayments.ts';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';

interface LoanScheduleProps {
  loanId: number;
}

interface ScheduleItem {
  id: number;
  period: number;
  due_date: string;
  principal_due: number;
  interest_due: number;
  total_payment_due: number;
  principal_paid: number;
  interest_paid: number;
  total_paid: number;
  balance_after_payment: number;
  status: 'pending' | 'partial' | 'paid' | 'overdue';
  is_overdue: boolean;
}

interface LoanScheduleData {
  loan: {
    id: number;
    loan_no: string;
    member_name: string;
    principal: number;
    interest: number;
    total_amount: number;
    loan_balance: number;
    status: string;
  };
  schedules: ScheduleItem[];
  summary: {
    total_principal_due: number;
    total_interest_due: number;
    total_payment_due: number;
    total_principal_paid: number;
    total_interest_paid: number;
    total_paid: number;
    pending_count: number;
    paid_count: number;
    overdue_count: number;
  };
}

export default function LoanScheduleTable({ loanId }: LoanScheduleProps) {
  const [scheduleData, setScheduleData] = useState<LoanScheduleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSchedule();
  }, [loanId]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/memberloan/${loanId}/schedule`);
      setScheduleData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load loan schedule');
      toast.error('Failed to load loan schedule');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string, isOverdue: boolean) => {
    if (isOverdue && status !== 'paid') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
          <AlertCircle size={12} className="mr-1" />
          Overdue
        </span>
      );
    }

    const statusConfig = {
      pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
      partial: { label: 'Partial', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
      paid: { label: 'Paid', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const handleExport = async () => {
    try {
      toast.success('Export functionality coming soon');
    } catch (err) {
      toast.error('Failed to export schedule');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !scheduleData) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertCircle size={48} className="mx-auto text-yellow-600 mb-3" />
        <p className="text-yellow-800 font-medium mb-2">No Payment Schedule Available</p>
        <p className="text-sm text-yellow-700">
          {error || 'The payment schedule will be generated when the loan is disbursed.'}
        </p>
      </div>
    );
  }

  const { schedules, summary } = scheduleData;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm text-blue-700 mb-1">Total Installments</p>
          <p className="text-2xl font-bold text-blue-900">{schedules.length}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-sm text-green-700 mb-1">Paid</p>
          <p className="text-2xl font-bold text-green-900">{summary.paid_count}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <p className="text-sm text-yellow-700 mb-1">Pending</p>
          <p className="text-2xl font-bold text-yellow-900">{summary.pending_count}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <p className="text-sm text-red-700 mb-1">Overdue</p>
          <p className="text-2xl font-bold text-red-900">{summary.overdue_count}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center">
          <Calendar size={20} className="mr-2 text-blue-600" />
          Payment Schedule
        </h3>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center text-sm"
          >
            <Download size={16} className="mr-2" />
            Export
          </button>
          <button
            onClick={() => window.print()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center text-sm"
          >
            <FileText size={16} className="mr-2" />
            Print
          </button>
        </div>
      </div>

      {/* Schedule Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="responsive-table responsive-table-grid min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Principal Due
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interest Due
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Payment Due
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Principal Paid
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interest Paid
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Paid
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {schedules.map((schedule) => (
                <tr 
                  key={schedule.id} 
                  className={`hover:bg-gray-50 ${schedule.is_overdue && schedule.status !== 'paid' ? 'bg-red-50' : ''}`}
                >
                  <td data-label="Period" className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {schedule.period}
                  </td>
                  <td data-label="Due Date" className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {new Date(schedule.due_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </td>
                  <td data-label="Principal Due" className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatCurrency(schedule.principal_due)}
                  </td>
                  <td data-label="Interest Due" className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatCurrency(schedule.interest_due)}
                  </td>
                  <td data-label="Total Payment Due" className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                    {formatCurrency(schedule.total_payment_due)}
                  </td>
                  <td data-label="Principal Paid" className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                    {formatCurrency(schedule.principal_paid)}
                  </td>
                  <td data-label="Interest Paid" className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                    {formatCurrency(schedule.interest_paid)}
                  </td>
                  <td data-label="Total Paid" className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-green-600">
                    {formatCurrency(schedule.total_paid)}
                  </td>
                  <td data-label="Balance" className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatCurrency(schedule.balance_after_payment)}
                  </td>
                  <td data-label="Status" className="px-6 py-4 whitespace-nowrap text-center">
                    {getStatusBadge(schedule.status, schedule.is_overdue)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr className="font-semibold">
                <td colSpan={2} className="px-6 py-4 text-sm text-gray-900">
                  Totals
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                  {formatCurrency(summary.total_principal_due)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                  {formatCurrency(summary.total_interest_due)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                  {formatCurrency(summary.total_payment_due)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                  {formatCurrency(summary.total_principal_paid)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                  {formatCurrency(summary.total_interest_paid)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-green-600">
                  {formatCurrency(summary.total_paid)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
